import * as t from 'io-ts';
import { assertNever } from 'src_common/common/assertNever';
import { MethodType, ParamsFetchType, GenerateUrlApiType, ResponseType } from 'src_common/browser/apiUtils';
import { GenerateUrlApiParamsType } from 'src_common/server/webDriver/sdkApiWebUtils';
import { createGuard } from 'src_common/common/createGuard';

const Response200IO = t.array(
    t.interface({
        id: t.number,
        universe: t.string,
        card: t.string,
        accountId: t.number,
        ruleType: t.string,
        amount: t.union([t.undefined, t.number]),
        reason: t.union([t.undefined, t.array(t.string)]),
        note: t.union([t.undefined, t.string]),
    })
);

const getRule = (rule: 'approve' | 'reject'): string => {
    switch (rule) {
        case 'approve':
            return 'approve';
        case 'reject':
            return 'reject';
        default:
            return assertNever('action', rule);
    }
};

type Response200Type = Array<{
    id: number;
    universe: string;
    card: string;
    accountId: number;
    ruleType: string; // 'reject' | 'approve',
    amount?: number;
    reason?: Array<string>;
    note?: string;
}>;

const isResponse = createGuard(Response200IO);

export type WithdrawalRuleModelType = Response200Type extends Array<infer Model> ? Model : never;

const decode = (status: number, data: ResponseType): Array<WithdrawalRuleModelType> => {
    if (status === 200 && data.type === 'json') {
        if (isResponse(data.json)) {
            return data.json;
        }
        throw Error('Response decoding error /automatic-withdrawal-rule/{universe} -> get -> 200');
    } else if (status === 404) {
        console.error(`Not found endpoint /automatic-withdrawal-rule/{universe} -> get -> ${status}`);
        return [];
    }

    throw new Error(`unhandled response ${status} - ${data.type}`);
};

interface GetRulesType {
    ruleType: 'reject' | 'approve';
}

export const getWithdrawalRules = {
    browser: {
        params: (params: GetRulesType): ParamsFetchType<GetRulesType> => {
            return {
                type: MethodType.POST,
                url: '/api-web/automatic-withdrawal-rule/star/get-rules',
                body: {
                    ruleType: params.ruleType,
                },
            };
        },
        decode: decode,
    },
    express: {
        method: MethodType.POST,
        urlBrowser: '/api-web/automatic-withdrawal-rule/star/get-rules',
    },
    generateUrlApi: async (params: GenerateUrlApiParamsType<GetRulesType>): Promise<GenerateUrlApiType> => {
        if (params.userSessionId === null) {
            return {
                passToBackend: false,
                status: 403,
                responseBody: {
                    errorMessage: 'User id invalid',
                },
            };
        }

        const { ruleType } = params.req.body;

        const finalUrl = `${params.API_URL}/automatic-withdrawal-rule/${params.API_UNIVERSE}/${getRule(ruleType)}`;

        return {
            url: finalUrl,
            passToBackend: true,
            method: MethodType.GET,
        };
    },
};
